import { useMemo } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { type BouncerRoleUser } from "../../../services/bouncerUsers";
import { useUsersByRole } from "../hooks/useUsersByRole";

export type ReviewerSelection = {
  /** Every Category Manager named for this task. All are assigned; any one may act. */
  cm: BouncerRoleUser[];
};

type Props = {
  value: ReviewerSelection;
  onChange: (next: ReviewerSelection) => void;
  disabled?: boolean;
};


function optionLabel(u: BouncerRoleUser | null): string {
  if (!u) return "";
  return `${u.user_name} · ${u.email_id}`;
}

export function ReviewerAssignmentCard({ value, onChange, disabled }: Props) {
  const cmQuery = useUsersByRole("CATEGORY_MANAGER");

  const cmUsers = useMemo(() => cmQuery.data ?? [], [cmQuery.data]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        mt: 3,
        borderColor: "#E0E4EF",
        borderRadius: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
        Assign next reviewer
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Select the Category Managers for this task. Name more than one and any of them can review it. Business Finance and Compliance are routed to their configured approvers automatically.
      </Typography>

      {cmQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn&apos;t load reviewer options. Refresh to retry.
        </Alert>
      )}

      <Box sx={{ display: "grid", gap: 2 }}>
        <Autocomplete
          multiple
          filterSelectedOptions
          options={cmUsers}
          value={value.cm}
          onChange={(_, next) => onChange({ ...value, cm: next })}
          getOptionLabel={optionLabel}
          isOptionEqualToValue={(a, b) => a.user_id === b?.user_id}
          disabled={disabled}
          loading={cmQuery.isFetching}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Category Manager"
              placeholder={value.cm.length ? "" : "Search by name or email"}
              required={value.cm.length === 0}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {cmQuery.isFetching && (
                      <CircularProgress size={16} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </Paper>
  );
}
